import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox, Tooltip } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import { Stack } from "@mui/system";

import { SearchField } from "@/components/SearchField";
import { Column } from "@/components/sort/SelectOrderByComponent";
import CSVExportButton from "@/pages/threats/components/CSVExportButton";
import DetailPaneHeader from "@/pages/threats/components/DetailPaneHeader";
import { FilterTools } from "@/pages/threats/components/FilterTools";
import {
  ThreatPagination,
  ThreatTable,
} from "@/pages/threats/components/PaginatedTable";
import { SortTools } from "@/pages/threats/components/SortTools";
import ThreatRow from "@/pages/threats/components/ThreatRow";
import ThreatTabs from "@/pages/threats/components/ThreatTabs";
import {
  ThreatsDetailsPane,
  ThreatsLayout,
  ThreatsListPane,
  ThreatsPaginationPane,
  ThreatsTitlePane,
  ThreatsToolPane,
} from "@/pages/threats/components/ThreatsLayout";
import Details from "@/pages/threats/components/details/Details";
import { useThreatService } from "@/pages/threats/service/service";
import { ThreatSourceParams } from "@/pages/threats/service/types";

const SelectAllCheckbox: React.FC<{
  isChecked: boolean;
  onToggle: (checked: boolean) => void;
}> = ({ isChecked, onToggle }) => {
  const { t } = useTranslation();
  const tooltipText = isChecked ? t("Unselect all") : t("Select all");
  return (
    <Tooltip title={tooltipText} placement="top">
      <Checkbox
        color="primary"
        checked={isChecked}
        onChange={(e) => onToggle(e.target.checked)}
      />
    </Tooltip>
  );
};

const SelectAllThreatsButton = ({
  allThreatsSelectedByPage,
  allThreatsSelected,
  threats,
  pagination,
  selectionList,
  setAllThreatsSelectedByPage,
}) => {
  const handleToggle = () => {
    const currentPage = pagination.currentPage;
    const allThreatsSelected = allThreatsSelectedByPage[currentPage] || false;
    const newValue = !allThreatsSelected;

    setAllThreatsSelectedByPage((prev) => ({
      ...prev,
      [currentPage]: newValue,
    }));
    if (newValue) {
      threats.forEach((t) => selectionList.addToSelection(t.id));
    } else {
      threats.forEach((t) => selectionList.removeFromSelection(t.id));
    }
  };

  return (
    <SelectAllCheckbox isChecked={allThreatsSelected} onToggle={handleToggle} />
  );
};

const Threats = ({ disableThreatsInBanners = false }) => {
  const {
    selectionList,
    pagination,
    listOptions,
    threats,
    totalCount,
    selectedThreats,
    unarchiveSelectedThreats,
    deleteSelectedThreats,
    sendFeedbackForSelectedThreats,
    search,
    onFilterChange,
    onSortChange,
    onTabChange,
    displayThreat,
    isFetching,
    detailsLoading,
    actDisplayThreat,
    allThreatsOnCurrentTab,
  } = useThreatService(disableThreatsInBanners);

  const { t } = useTranslation();

  const [onArchivedTab, setOnArchivedTab] = useState(false);
  const [allThreatsSelected, setAllThreatsSelected] = useState(false);
  const [allThreatsSelectedByPage, setAllThreatsSelectedByPage] = useState<
    Record<number, boolean>
  >({});

  const columns: Column[] = [
    { label: t("Report Date"), name: "report_date" },
    { label: t("Banner level"), name: "banner_level" },
    { label: t("Source"), name: "source" },
    { label: t("Sender"), name: "sender" },
    { label: t("Recipient"), name: "recipient" },
  ];

  useEffect(() => {
    const currentPage = pagination.currentPage;
    const isAllSelected = allThreatsSelectedByPage[currentPage] || false;
    setAllThreatsSelected(isAllSelected);
  }, [pagination.currentPage, allThreatsSelectedByPage, selectionList]);

  return (
    <ThreatsLayout>
      <ThreatsTitlePane>
        <ThreatTabs
          disableBannersInThreats={disableThreatsInBanners}
          onChange={(tab: ThreatSourceParams) => {
            onTabChange(tab);
            setOnArchivedTab(tab.archived);
          }}
        />
        <DetailPaneHeader
          areThreatsArchived={onArchivedTab}
          selectedThreats={selectedThreats}
          onDelete={deleteSelectedThreats}
          onUnarchive={unarchiveSelectedThreats}
          onSendFeedback={sendFeedbackForSelectedThreats}
        />
      </ThreatsTitlePane>
      <ThreatsToolPane>
        <SelectAllThreatsButton
          allThreatsSelectedByPage={allThreatsSelectedByPage}
          allThreatsSelected={allThreatsSelected}
          threats={threats}
          pagination={pagination}
          selectionList={selectionList}
          setAllThreatsSelectedByPage={setAllThreatsSelectedByPage}
        />
        <Stack direction="row" spacing={1}>
          <SearchField
            value={listOptions.searchTerm}
            onChange={(e) => search(e.target.value)}
          />
          <FilterTools value={listOptions} onChange={onFilterChange} />
          <SortTools onChange={onSortChange} columns={columns} />
        </Stack>
        <CSVExportButton getAllThreats={allThreatsOnCurrentTab} />
      </ThreatsToolPane>
      <ThreatsListPane>
        <ThreatTable data={threats} isLoading={isFetching}>
          <TableBody>
            {threats.map((threat) => (
              <ThreatRow
                key={threat.id}
                threat={threat}
                displayThreat={actDisplayThreat}
                isDisplayed={displayThreat?.id === threat.id}
                selectionList={selectionList}
              />
            ))}
          </TableBody>
        </ThreatTable>
      </ThreatsListPane>
      <ThreatsPaginationPane>
        <ThreatPagination pagination={pagination} totalCount={totalCount} />
      </ThreatsPaginationPane>
      <ThreatsDetailsPane>
        <Details isLoading={detailsLoading} threat={displayThreat} />
      </ThreatsDetailsPane>
    </ThreatsLayout>
  );
};

export default Threats;
